import { MutationFunction, QueryKey, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useCustomSnackBar } from '../shared/SnackBar/useCustomSnackBar';

/** UI-side options a component passes to an API file's mutation hook. */
export type ApiMutationUiOptions<TData = unknown, TVariables = void> = {
  successMessage?: string;
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
  /** Runs after success or failure alike, e.g. to close a dialog regardless of outcome. */
  onSettled?: () => void;
};

export type ApiMutationOptions<TData, TVariables> = ApiMutationUiOptions<TData, TVariables> & {
  /** Maps a thrown API exception to the domain error whose message is shown, e.g. getShopsApiError. */
  getError: (error: unknown) => Error;
  invalidateKeys?: QueryKey[];
};

/** Only for building mutation hooks inside the API files; components use those hooks instead. */
export const useApiMutation = <TData = unknown, TVariables = void>(
  mutationFn: MutationFunction<TData, TVariables>,
  { getError, invalidateKeys, successMessage, onSuccess, onSettled }: ApiMutationOptions<TData, TVariables>,
): UseMutationResult<TData, unknown, TVariables> => {
  const snackBar = useCustomSnackBar();
  const queryClient = useQueryClient();

  return useMutation(mutationFn, {
    onSuccess: async (data, variables) => {
      if (invalidateKeys) {
        await Promise.all(invalidateKeys.map((key) => queryClient.invalidateQueries(key)));
      }
      if (successMessage) {
        snackBar.showSuccess(successMessage);
      }
      await onSuccess?.(data, variables);
    },
    onError: (error) => snackBar.showError(getError(error).message),
    onSettled,
  });
};
